/**
 * `useRuntimeVersion` — §C consumer of the lexserver §B
 * `GET /api/lx/version-skew` (per-user). Drives the Settings "AI Engine"
 * runtime-version line + the state-driven UPGRADE button.
 *
 * Read-mostly: the skew changes only on a runtime re-provision, so a
 * session module-cache + an explicit `refresh()` (called after a triggered
 * upgrade) is plenty. Fail-soft: a transport error leaves `skew: null`
 * (UI shows no version / no enabled button) — it must never crash or
 * imply "up to date".
 */

import { useEffect, useState, useCallback } from "react";
import {
  fetchRuntimeVersionSkew,
  type RuntimeVersionSkew,
} from "@/lib/lexserver-client";

let cache: RuntimeVersionSkew | null = null;
let inFlight: Promise<RuntimeVersionSkew> | null = null;

/** Drop the cached skew (after an upgrade trigger / sign-out / tests). */
export function clearRuntimeVersionCache(): void {
  cache = null;
  inFlight = null;
}

export interface UseRuntimeVersionResult {
  skew: RuntimeVersionSkew | null;
  loading: boolean;
  /** Re-fetch (e.g. after triggering an upgrade). */
  refresh: () => void;
}

export function useRuntimeVersion(): UseRuntimeVersionResult {
  const [skew, setSkew] = useState<RuntimeVersionSkew | null>(cache);
  const [loading, setLoading] = useState(cache == null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    clearRuntimeVersionCache();
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (cache) {
      setSkew(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const pending = inFlight ?? (inFlight = fetchRuntimeVersionSkew());
    pending
      .then((s) => {
        cache = s;
        if (!cancelled) {
          setSkew(s);
          setLoading(false);
        }
      })
      .catch(() => {
        if (inFlight === pending) inFlight = null;
        if (!cancelled) {
          setSkew(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { skew, loading, refresh };
}
